import { Grid, useTheme, Button, Box, CircularProgress, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useContext, useState, useRef, useEffect } from 'react';
import * as yup from 'yup';
import MuiDialog from '../../../SystemDesign/MuiDialog';
import FormInput from '../../../SystemDesign/FormInput';
import AddIcon from '@mui/icons-material/Add';
import { tokens } from '../../../theme';
import { PrimaryButton } from '../../../SystemDesign/Styles';
import { SnackBarContext } from '../../../Context/SnackBarContextProvider';
import { useCreateRaterTypeMutation } from '../../../services/SkillsManagement';
import { useDispatch } from 'react-redux';
import { ADD_RATER_TYPE } from '../../../Reducers/Actions';

const AddRaterType = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  const [createRaterType] = useCreateRaterTypeMutation();
  const [close, setClose] = useState(false);
  const formikRef = useRef(null);
  const dispatch = useDispatch();

  const initialValues = {
    name: '',
    description: ''
  };

  const [formValues, setFormValues] = useState(initialValues);

  // Reset form values when dialog closes
  useEffect(() => {
    if (close) {
      setFormValues(initialValues);
      setClose(false);
    }
  }, [close]);

  const formFields = [
    { type: 'text', name: 'name', label: 'Name', required: true },
    { type: 'text', name: 'description', label: 'Description', required: false }
  ];

  const formValidationSchema = yup.object().shape({
    name: yup
      .string()
      .required('Name is required')
      .min(2, 'Name must be at least 2 characters'),
    description: yup.string().nullable()
  });

  const handleSubmit = async (values, actions) => {
    try {
      const payload = {
        name: values.name,
        // Send null when description is empty to match schema allowing null
        ...(values.description ? { description: values.description } : { description: null })
      };

      const response = await createRaterType({ body: payload }).unwrap();

      // Dispatch to Redux store like AddRole does
      if (response?.raterType) {
        dispatch({ type: ADD_RATER_TYPE, payload: response.raterType });
      }

      actions.resetForm();
      setFormValues(initialValues);
      setInfoAlert({ severity: 'success', message: `Rater type ${values.name} created successfully` });
      setOpenSnackBar(true);
      setClose(true);
    } catch (error) {
      setInfoAlert({ severity: 'error', message: error?.data?.message || 'An error occurred while creating the rater type' });
      setOpenSnackBar(true);
    }
    actions.setSubmitting(false);
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '2rem' }}>
      <MuiDialog
        icon={<AddIcon />}
        triggerButton={
          <Button variant="contained" sx={PrimaryButton(colors)} endIcon={<AddIcon />}> 
            Rater Type
          </Button>
        }
        maxWidth="sm"
        title="New Rater Type"
        subtitle="Create a new rater type"
        content={
          <Formik
            initialValues={formValues}
            validationSchema={formValidationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize={true}
            onSubmit={handleSubmit}
            innerRef={formikRef}
          >
            {(formik) => (
              <Form id="createRaterTypeForm">
                <Grid container spacing={2} marginTop={2}>
                  {formFields.map((field, idx) => (
                    <Grid item size={{ xs: 12 }} key={idx}>
                      <FormInput
                        type={field.type}
                        name={field.name}
                        label={field.label}
                        required={field.required}
                        value={formik.values[field.name]}
                        onChange={(e) => {
                          formik.handleChange(e);
                          setFormValues((prev) => ({ ...prev, [field.name]: e.target.value }));
                        }}
                        error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                        helperText={formik.touched[field.name] && formik.errors[field.name]}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Form>
            )}
          </Formik>
        }
        existAction={true}
        actionTitle="Create"
        actionFunction={() => {
          const formEl = document.querySelector('form');
          if (formEl) formEl.requestSubmit();
        }}
        onClose={close}
      />
    </div>
  );
};

export default AddRaterType;
